import React, { useEffect, useMemo } from 'react';

import {
  Section,
  Stack,
  Button,
  Help,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  normalizeSelectValue,
} from '@gogame/components';
import {
  withForm,
  FormField,
  SubmitButton,
  withFormField,
  omitFormProps,
} from '@gogame/form';

import {
  AD_ID,
  BUSINESS_PARTNER,
  APP_ID,
  EVENT_TYPES,
  UM_ID,
  PERIOD,
  POSTBACK_AND_CONVERSIONS_STATUSES,
  APPSFLYER_ID,
  PLAYER_ID,
  PARTNER_CLICK_ID,
  REGULAR_EVENT,
  RECURRENT_EVENT,
  PROMO_CODES,
  REG_ACTION_ID,
} from 'scenes/PlayersActivities/PlayersInformation/constants';
import { addRequestForm } from 'scenes/PlayersActivities/PlayersInformation/schemas/addRequestForm';
import {
  loadBusinessPartners,
  loadApplications,
  loadRegularEvents,
  loadRecurrentEvents,
  loadPromoCodes,
} from 'scenes/PlayersActivities/PlayersInformation/sources/fields';

import { Container, Content, Footer } from '../Container';

const DEPENDENCIES = {
  [EVENT_TYPES]: [APP_ID],
  [PROMO_CODES]: [APP_ID],
};

const EMPTY_OPTIONS = [];

const RecurrentEventSelect = withFormField({
  normalizeValue: normalizeSelectValue,
})(({ area, value: _value, onChange, ...props }) => {
  const handleChange = (e, value) => {
    onChange(e, value ? [value] : []);
  };

  return (
    <Select
      loading={area.loading}
      options={area.data || EMPTY_OPTIONS}
      value={_value?.[0]}
      onChange={handleChange}
      {...omitFormProps(props)}
    />
  );
});

const AddRequestForm = withForm({
  enableReinitialize: true,
  validationSchema: addRequestForm,
})(({ values, setFieldValue, resetForm }) => {
  const isShowBusinessPartner = useMemo(
    () => !!values[POSTBACK_AND_CONVERSIONS_STATUSES],
    [values[POSTBACK_AND_CONVERSIONS_STATUSES]]
  );

  useEffect(() => {
    if (
      !values[POSTBACK_AND_CONVERSIONS_STATUSES] &&
      values[BUSINESS_PARTNER] !== undefined
    ) {
      setFieldValue(BUSINESS_PARTNER, undefined);
    }
  }, [
    setFieldValue,
    values[BUSINESS_PARTNER],
    values[POSTBACK_AND_CONVERSIONS_STATUSES],
  ]);
  const handleClear = () => {
    resetForm({});
  };

  const [eventType, setEventType] = React.useState(REGULAR_EVENT);

  const handleChangeEventType = (_, value) => {
    setEventType(value);
  };

  const handleEventOptionDisabled = (opt, selected) => {
    return selected?.length >= 15 && !selected.includes(opt);
  };

  return (
    <Container>
      <Content>
        <Section title={'Account'}>
          <Stack spacing={3}>
            <FormField.Select
              name={APP_ID}
              label={'Application'}
              source={loadApplications}
              padding={'0'}
              required={true}
            />
            <FormField.TextField
              name={AD_ID}
              label={'Advertising ID'}
              padding={'0'}
            />
            <FormField.TextField
              name={REG_ACTION_ID}
              label={'Reg Action ID'}
              padding={'0'}
            />
            <FormField.TextField name={UM_ID} label={'UID'} padding={'0'} />
            <FormField.TextField name={PLAYER_ID} label={'Player ID'} padding={'0'} />
            <FormField.TextField
              name={APPSFLYER_ID}
              label={'Appsflyer ID'}
              padding={'0'}
            />
            <FormField.TextField
              name={PARTNER_CLICK_ID}
              label={'Partner Click ID'}
              padding={'0'}
              helperText={'For mobile'}
            />
          </Stack>
        </Section>
        <Section title={'Events'}>
          <Stack spacing={3}>
            <FormField.DateRangePicker
              name={PERIOD}
              padding={0}
              label={'Period'}
            />
            <ToggleButtonGroup
              size={'small'}
              color={'primary'}
              exclusive={true}
              onChange={handleChangeEventType}
              value={eventType}
            >
              <ToggleButton value={REGULAR_EVENT}>Regular event</ToggleButton>
              <ToggleButton value={RECURRENT_EVENT}>
                Recurrent event
              </ToggleButton>
            </ToggleButtonGroup>

            {eventType === REGULAR_EVENT ? (
              <FormField.MultiSelect
                name={EVENT_TYPES}
                depends={DEPENDENCIES[EVENT_TYPES]}
                source={loadRegularEvents}
                padding={'0'}
                placeholder={'All'}
                label={'Regular Event'}
                active={values[APP_ID] !== undefined}
                isOptionDisabled={handleEventOptionDisabled}
                helper={'You can select up to 15 events.'}
                withSelectAll={false}
              />
            ) : null}
            {eventType === RECURRENT_EVENT ? (
              <RecurrentEventSelect
                name={EVENT_TYPES}
                depends={DEPENDENCIES[EVENT_TYPES]}
                source={loadRecurrentEvents}
                padding={'0'}
                placeholder={'All'}
                label={'Recurrent Event'}
                active={values[APP_ID] !== undefined}
                helper={
                  'Data will be available for the first 30 days from the registration date.'
                }
              />
            ) : null}
            <FormField.Checkbox
              name={POSTBACK_AND_CONVERSIONS_STATUSES}
              placeholder={'All'}
              label={
                <Stack as={'span'} spacing={0.5} direction={'row'}>
                  <span> Postback and Conversions statuses</span>
                  <Help>
                    Shows "No" if event was completed more than 60 days ago.
                  </Help>
                </Stack>
              }
              padding={'0'}
            />
            {isShowBusinessPartner ? (
              <FormField.Select
                name={BUSINESS_PARTNER}
                source={loadBusinessPartners}
                placeholder={'All'}
                label={'Business Partner'}
                required={true}
                padding={'0'}
              />
            ) : null}
          </Stack>
        </Section>
        <Section title={'Promo Codes'}>
          <Stack spacing={3}>
            <FormField.MultiSelect
              name={PROMO_CODES}
              depends={DEPENDENCIES[PROMO_CODES]}
              active={values[APP_ID] !== undefined}
              source={loadPromoCodes}
              padding={'0'}
              placeholder={'All'}
              label={'Promo codes'}
            />
          </Stack>
        </Section>
      </Content>

      <Footer justifyContent={'space-between'}>
        <Button onClick={handleClear} role={'button'}>
          Clear
        </Button>
        <SubmitButton
          title={'Apply'}
          disabled={
            !values[PLAYER_ID] &&
            !values[AD_ID] &&
            !values[UM_ID] &&
            !values[REG_ACTION_ID] &&
            !values[APPSFLYER_ID] &&
            !values[PARTNER_CLICK_ID]
          }
        />
      </Footer>
    </Container>
  );
});

export default AddRequestForm;
